import { useState } from "react";
import { Button, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  DateField,
  FunctionField,
  Show,
  SimpleShowLayout,
  TextField,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";

import { adminApi } from "../../dataProvider";
import { API_URL } from "../../config";
import { withdrawalStatus } from "./status";

type WithdrawalRecord = {
  id: string;
  reference: string;
  method: "card" | "swift" | "crypto";
  method_label: string;
  destination: Record<string, string>;
  amount_rub: number | null;
  rate: number | null;
  amount_usdt: number;
  fee_usdt: number;
  net_usdt: number;
  status: string;
  cancel_reason: string | null;
  tx_hash: string | null;
  invoice: { id: string; mime: string; size: number; url: string } | null;
};

// подписи полей реквизитов назначения по способу вывода
const DEST_ROWS: Record<string, { key: string; label: string }[]> = {
  card: [
    { key: "card_number", label: "Номер карты" },
    { key: "first_name", label: "Имя" },
    { key: "last_name", label: "Фамилия" },
  ],
  swift: [
    { key: "beneficiary_name", label: "Получатель" },
    { key: "iban", label: "IBAN / счёт" },
    { key: "swift", label: "SWIFT/BIC" },
    { key: "bank_name", label: "Банк" },
    { key: "bank_country", label: "Страна банка" },
    { key: "purpose", label: "Назначение платежа" },
  ],
  crypto: [
    { key: "wallet_address", label: "Кошелёк (TRC20)" },
    { key: "network", label: "Сеть" },
  ],
};

function StatusChip() {
  const record = useRecordContext<WithdrawalRecord>();
  if (!record) return null;
  const s = withdrawalStatus(record.status);
  return <Chip size="small" color={s.color} label={s.label} />;
}

function ActionButtons() {
  const record = useRecordContext<WithdrawalRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  if (!record) return null;
  const st = record.status;

  const call = async (action: "complete" | "reject", json?: unknown, okMsg?: string) => {
    setBusy(true);
    try {
      await adminApi(`/admin/withdrawals/${record.id}/${action}`, { method: "POST", json });
      notify(okMsg ?? "Готово", { type: "success" });
      refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Ошибка", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const complete = () => {
    // для крипто можно приложить хэш транзакции (покажем клиенту)
    let tx_hash: string | null = null;
    if (record.method === "crypto") {
      tx_hash = window.prompt("Хэш транзакции (необязательно):") || null;
    }
    call("complete", tx_hash ? { tx_hash } : {}, "Вывод отмечен выполненным");
  };

  const reject = () => {
    const reason = window.prompt("Причина отклонения (увидит клиент, средства вернутся на карту):");
    if (reason === null) return;
    call("reject", { reason }, "Заявка отклонена, средства возвращены");
  };

  if (st === "completed") return <Chip color="success" icon={<CheckCircleIcon />} label="Выполнена" />;
  if (st === "rejected") return <Chip color="error" icon={<CancelIcon />} label="Отклонена" />;
  if (st === "cancelled") return <Chip icon={<CancelIcon />} label="Отменена клиентом" />;

  // Крипто-вывод исполняется автоматически (обмен → withdraw → форвард) — оператор не
  // подтверждает и не отклоняет; показываем статус вместо кнопок.
  if (record.method === "crypto") {
    return <Chip color="info" label={st === "processing" ? "Автоматический вывод — отправляется" : "Автоматический вывод — в очереди"} />;
  }

  return (
    <Stack direction="row" spacing={2}>
      <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} disabled={busy} onClick={complete}>
        Выполнить
      </Button>
      <Button variant="outlined" color="error" startIcon={<CancelIcon />} disabled={busy} onClick={reject}>
        Отклонить
      </Button>
    </Stack>
  );
}

function DestinationBlock() {
  const record = useRecordContext<WithdrawalRecord>();
  if (!record) return null;
  const rows = DEST_ROWS[record.method] ?? [];
  const dest = record.destination ?? {};
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        {rows.map((row) => (
          <Stack key={row.key} direction="row" spacing={2}>
            <Typography variant="caption" sx={{ width: 160, color: "text.secondary" }}>
              {row.label}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
              {dest[row.key] ?? "—"}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

function InvoiceBlock() {
  const record = useRecordContext<WithdrawalRecord>();
  const inv = record?.invoice;
  if (!inv) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const href = API_URL + inv.url;
  const isImage = inv.mime.startsWith("image/");
  return (
    <Paper variant="outlined" sx={{ p: 1.5, maxWidth: 360 }}>
      {isImage ? (
        <a href={href} target="_blank" rel="noreferrer">
          <img
            src={href}
            alt="invoice"
            style={{ display: "block", width: "100%", maxHeight: 320, objectFit: "contain", background: "#000", borderRadius: 4 }}
          />
        </a>
      ) : (
        <Button variant="outlined" href={href} target="_blank" rel="noreferrer">
          Открыть инвойс (PDF)
        </Button>
      )}
      <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
        {inv.mime} · {Math.round(inv.size / 1024)} KB
      </Typography>
    </Paper>
  );
}

export function WithdrawalShow() {
  return (
    <Show>
      <SimpleShowLayout>
        <TextField source="reference" label="Заявка" />
        <FunctionField label="Статус" render={() => <StatusChip />} />
        <FunctionField label="Действие" render={() => <ActionButtons />} />
        <FunctionField
          label="Причина отклонения"
          render={(r: { status: string; cancel_reason: string | null }) =>
            r.status === "rejected" ? (
              <Typography variant="body2" color="error">
                {r.cancel_reason || "—"}
              </Typography>
            ) : null
          }
        />
        <Divider />
        <TextField source="method_label" label="Способ" />
        <TextField source="source_label" label="Источник" emptyText="Счёт" />
        <FunctionField
          label="Списано, ₽"
          render={(r: { amount_rub: number | null; source?: string }) =>
            r.source === "card"
              ? "— (списано с карты)"
              : r.amount_rub != null
                ? r.amount_rub.toLocaleString("ru-RU")
                : "—"
          }
        />
        <FunctionField
          label="Курс, ₽/USD"
          render={(r: { rate: number | null }) => (r.rate ? r.rate.toFixed(2) : "—")}
        />
        <FunctionField label="Брутто, USD" render={(r: { amount_usdt: number }) => r.amount_usdt.toFixed(2)} />
        <FunctionField label="Комиссия, USD" render={(r: { fee_usdt: number }) => r.fee_usdt.toFixed(2)} />
        <FunctionField label="К выплате, USD" render={(r: { net_usdt: number }) => r.net_usdt.toFixed(2)} />
        <TextField source="user_phone" label="Телефон" />
        <FunctionField
          label="Хэш транзакции"
          render={(r: { tx_hash: string | null }) =>
            r.tx_hash ? (
              <a href={`https://tronscan.org/#/transaction/${r.tx_hash}`} target="_blank" rel="noreferrer">
                {r.tx_hash}
              </a>
            ) : (
              "—"
            )
          }
        />
        <DateField source="created_at" label="Создана" showTime />
        <DateField source="completed_at" label="Выполнена" showTime />
        <FunctionField label="Реквизиты назначения" render={() => <DestinationBlock />} />
        <FunctionField label="Инвойс" render={() => <InvoiceBlock />} />
      </SimpleShowLayout>
    </Show>
  );
}
